import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { ChevronRight, ArrowLeft, Calendar, Clock, Tag, BookOpen, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  cover_image_url: string | null;
  category: string;
  tags: string[];
  author_name: string;
  reading_time_minutes: number;
  meta_title: string | null;
  meta_description: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })
    : "";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      return data as BlogPost | null;
    },
    enabled: !!slug,
  });

  const { data: related = [] } = useQuery({
    queryKey: ["blog-related", post?.category, slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, category, cover_image_url, reading_time_minutes")
        .eq("published", true)
        .eq("category", post!.category)
        .neq("slug", slug!)
        .order("published_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!post,
  });

  if (isLoading) {
    return (
      <PageLayout>
        <div className="container py-20">
          <div className="mx-auto max-w-3xl space-y-4">
            <div className="h-8 w-48 animate-pulse rounded bg-muted" />
            <div className="h-12 w-full animate-pulse rounded bg-muted" />
            <div className="h-64 w-full animate-pulse rounded-2xl bg-muted" />
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-4 animate-pulse rounded bg-muted" style={{ width: `${70 + Math.random() * 30}%` }} />
              ))}
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!post) {
    return (
      <PageLayout>
        <div className="container py-20 text-center">
          <h1 className="mb-4 text-2xl font-bold text-foreground">Artikel nicht gefunden</h1>
          <Button asChild variant="outline">
            <Link to="/blog">Zurück zum Blog</Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  const metaTitle = post.meta_title || `${post.title} – NextInvoice Blog`;
  const metaDesc = post.meta_description || post.excerpt;

  return (
    <PageLayout>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={post.tags.join(", ")} />
        <link rel="canonical" href={`https://nxtinvoice.de/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={`https://nxtinvoice.de/blog/${post.slug}`} />
        <meta property="og:site_name" content="NextInvoice" />
        {post.cover_image_url && <meta property="og:image" content={post.cover_image_url} />}
        <meta property="article:section" content={post.category} />
        <meta property="article:published_time" content={post.published_at || post.created_at} />
        <meta property="article:modified_time" content={post.updated_at} />
        {post.tags.map((t) => (
          <meta key={t} property="article:tag" content={t} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDesc} />
        {post.cover_image_url && <meta name="twitter:image" content={post.cover_image_url} />}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: metaDesc,
            image: post.cover_image_url || undefined,
            datePublished: post.published_at || post.created_at,
            dateModified: post.updated_at,
            inLanguage: "de-DE",
            articleSection: post.category,
            keywords: post.tags.join(", "),
            wordCount: post.content.replace(/<[^>]+>/g, " ").trim().split(/\s+/).length,
            author: { "@type": "Person", name: post.author_name },
            publisher: {
              "@type": "Organization",
              name: "NextInvoice",
              logo: { "@type": "ImageObject", url: "https://nxtinvoice.de/favicon.svg" },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `https://nxtinvoice.de/blog/${post.slug}`,
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Startseite", item: "https://nxtinvoice.de/" },
              { "@type": "ListItem", position: 2, name: "Blog", item: "https://nxtinvoice.de/blog" },
              { "@type": "ListItem", position: 3, name: post.title, item: `https://nxtinvoice.de/blog/${post.slug}` },
            ],
          })}
        </script>
      </Helmet>

      {/* Header */}
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/5 via-background to-accent/5 pb-12 pt-12 lg:pt-16">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="container relative">
          {/* Breadcrumb */}
          <nav className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/" className="transition-colors hover:text-foreground">Startseite</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to="/blog" className="transition-colors hover:text-foreground">Blog</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="font-medium text-foreground line-clamp-1">{post.title}</span>
          </nav>

          <div className="mx-auto max-w-4xl text-center">
            <Badge variant="secondary" className="mb-4 rounded-full">{post.category}</Badge>
            <h1 className="mb-6 font-display text-4xl font-bold tracking-tight text-foreground lg:text-5xl text-balance">
              {post.title}
            </h1>
            <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              {post.excerpt}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><User className="h-4 w-4" />{post.author_name}</span>
              <span className="flex items-center gap-2"><Calendar className="h-4 w-4" />{formatDate(post.published_at || post.created_at)}</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" />{post.reading_time_minutes} Min. Lesezeit</span>
            </div>
          </div>
        </div>
      </section>

      {/* Cover */}
      {post.cover_image_url && /^https:\/\//i.test(post.cover_image_url) && (
        <div className="container -mt-2 relative z-10">
          <div className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-border shadow-2xl">
            <img
              src={post.cover_image_url}
              alt={post.title}
              loading="eager"
              className="aspect-[16/9] w-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="container py-12 lg:py-16">
        <div className="flex flex-col gap-12 lg:flex-row">
          <article className="min-w-0 flex-1">
            <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-10">
              <div
                className="prose prose-base max-w-none
                  prose-headings:font-display prose-headings:text-foreground
                  prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-muted-foreground prose-p:leading-relaxed
                  prose-li:text-muted-foreground
                  prose-strong:text-foreground
                  prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-primary/5 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:not-italic prose-blockquote:text-foreground
                  prose-img:rounded-xl prose-img:border prose-img:border-border
                  prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm prose-code:before:content-none prose-code:after:content-none
                  prose-hr:border-border"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
              />

              {post.tags.length > 0 && (
                <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-border pt-6">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tags:</span>
                  {post.tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground"
                    >
                      <Tag className="h-3 w-3" />
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="mx-auto mt-8 flex max-w-3xl items-center justify-between">
              <Button asChild variant="outline" size="sm" className="rounded-xl">
                <Link to="/blog">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Alle Artikel
                </Link>
              </Button>
              <Button asChild size="sm" className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90">
                <Link to="/konfigurator">
                  Konfigurator öffnen
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </article>

          {/* Sidebar */}
          {related.length > 0 && (
            <aside className="w-full shrink-0 lg:w-80">
              <div className="sticky top-28 overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <div className="bg-gradient-to-r from-primary/10 to-accent/10 px-5 py-4">
                  <h3 className="flex items-center gap-2 font-display text-sm font-semibold text-foreground">
                    <BookOpen className="h-4 w-4 text-primary" />
                    Mehr aus {post.category}
                  </h3>
                </div>
                <ul className="divide-y divide-border">
                  {related.map((r: any) => (
                    <li key={r.id}>
                      <Link
                        to={`/blog/${r.slug}`}
                        className="group flex gap-3 p-4 transition-colors hover:bg-muted/30"
                      >
                        <div className="aspect-square w-16 shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-primary/10 to-accent/10">
                          {r.cover_image_url ? (
                            <img src={r.cover_image_url} alt={r.title} loading="lazy" className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full items-center justify-center">
                              <BookOpen className="h-5 w-5 text-primary/40" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="line-clamp-2 text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
                            {r.title}
                          </h4>
                          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {r.reading_time_minutes} Min.
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default BlogPost;
